import {
  AvailableWhatsappTier,
  GetWhatsappTier,
  GetWhatsappTierRange,
  GetWhatsappTierStatusGroup,
  PostWhatsappTier,
  PutWhatsappTier,
} from 'src/types/meta/whatsappTiers';
import { HttpClient } from '../common/httpClient';

export type GetWhatsappTierRangeResponse = {
  [countryName: string]: GetWhatsappTierStatusGroup;
};

export interface SelectedWhatsappTierRange {
  countryName: string;
  country: { callingCode: number | null | undefined },
  validFrom: string | null;
  validTo: string | null;
  status: 'ACTIVE' | 'PAST' | 'FUTURE' | null;
  utilityTierRanges: GetWhatsappTier[];
  authenticationTierRanges: GetWhatsappTier[];
  authInternationalTierRanges: GetWhatsappTier[];
  authInternationalEnabled?: boolean;
};

export type GetAvailableWhatsappTierRangeResponse = {
  countries: {
    [countryCallingCode: string]: AvailableWhatsappTier;
  }
};

export class WhatsappTierRangesApi {
  constructor(private readonly httpClient: HttpClient) { }

  private mapToGetWhatsappTiers(response: GetWhatsappTierRangeResponse): GetWhatsappTierRangeResponse {
    const result: GetWhatsappTierRangeResponse = {};

    for (const [countryName, statusGroups] of Object.entries(response)) {
      result[countryName] = {};

      for (const [status, tiers] of Object.entries(statusGroups)) {
        if (!tiers) continue;

        result[countryName][status as 'ACTIVE' | 'PAST' | 'FUTURE'] = tiers.map(tier => ({
          ...tier,
          status: status as 'ACTIVE' | 'PAST' | 'FUTURE',
          schedule: {
            validFrom: tier.schedule.validFrom,
            validTo: tier.schedule.validTo,
          },
        }));
      }
    }

    return result;
  }

  private formatWithCommas(value: number): string {
    if (value == null) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private mapToSelectedWhatsappTierRange(
    response: GetWhatsappTierRange
  ): SelectedWhatsappTierRange {
    const countryName = Object.keys(response)[0];
    if (!countryName) {
      return {
        countryName: '',
        country: { callingCode: null },
        validFrom: null,
        validTo: null,
        status: null,
        utilityTierRanges: [],
        authenticationTierRanges: [],
        authInternationalTierRanges: [],
        authInternationalEnabled: false,
      };
    }

    const statusGroups = response[countryName];
    const firstStatus = Object.keys(statusGroups)[0] as keyof GetWhatsappTierStatusGroup;
    const entries = statusGroups[firstStatus] ?? [];

    const schedule =
      entries.length > 0
        ? {
          validFrom: entries[0].schedule.validFrom,
          validTo: entries[0].schedule.validTo,
        }
        : { validFrom: null, validTo: null };

    const formatTiers = (tiers: any[]) =>
      tiers.map((t) => ({
        ...t,
        lower: this.formatWithCommas(t.lower),
        upper: this.formatWithCommas(t.upper),
      }));

    const utilityTierRanges = formatTiers(entries.filter((t) => t.category === 'UTILITY'));
    const authenticationTierRanges = formatTiers(entries.filter((t) => t.category === 'AUTHENTICATION'));
    const authInternationalTierRanges = formatTiers(entries.filter(
      (t) => t.category === 'AUTHENTICATION_INTERNATIONAL'
    ));

    return {
      countryName,
      country: {
        callingCode: entries.length > 0 ? entries[0].callingCode : null,
      },
      validFrom: schedule.validFrom,
      validTo: schedule.validTo,
      status: firstStatus,
      utilityTierRanges,
      authenticationTierRanges,
      authInternationalTierRanges,
      authInternationalEnabled:
        authInternationalTierRanges && authInternationalTierRanges.length > 0,
    };
  }

  private mapToAvailableWhatsappTierRange(response: GetAvailableWhatsappTierRangeResponse): GetAvailableWhatsappTierRangeResponse {
    return response;
  };

  getAllForAdmin(): Promise<GetWhatsappTierRange> {
    return this.httpClient.get<GetWhatsappTierRangeResponse>(`pricing/tier-range/`)
      .then(data => this.mapToGetWhatsappTiers(data));
  }

  getAllForUser(businessAccountId: string): Promise<GetWhatsappTierRange> {
    return this.httpClient.get<GetWhatsappTierRangeResponse>(`pricing/tier-range/${businessAccountId}`)
      .then(data => this.mapToGetWhatsappTiers(data));
  }

  async getAdminSelected(callingCode: number, validFrom: string, validTo: string): Promise<SelectedWhatsappTierRange> {
    let url = `pricing/tier-range/?callingCodes=${callingCode}&validFrom=${validFrom}`;
    if (validTo) url += `&validTo=${validTo}`;
    return await this.httpClient.get<GetWhatsappTierRange>(url)
      .then((data) => this.mapToSelectedWhatsappTierRange(data));
  }

  async getUserSelected(businessAccountId: string, callingCode: number, validFrom: string, validTo: string): Promise<SelectedWhatsappTierRange> {
    let url = `pricing/tier-range/${businessAccountId}?callingCodes=${callingCode}&validFrom=${validFrom}`;
    if (validTo) url += `&validTo=${validTo}`;
    return await this.httpClient.get<GetWhatsappTierRange>(url)
      .then((data) => this.mapToSelectedWhatsappTierRange(data));
  }

  async getAvailableSelected(callingCode: number): Promise<GetAvailableWhatsappTierRangeResponse> {
    return await this.httpClient.get<GetAvailableWhatsappTierRangeResponse>(`pricing/tier-range/available?callingCodes=${callingCode}`)
      .then((data) => this.mapToAvailableWhatsappTierRange(data));
  }

  async add(values: PostWhatsappTier): Promise<void> {
    await this.httpClient.post<PostWhatsappTier>(`pricing/tier-range/`, values);
  }

  async update(item: PutWhatsappTier[]): Promise<void> {
    await this.httpClient.put<PutWhatsappTier[]>(`pricing/tier-range/`, item);
  }

  async delete(callingCode: number, validFrom: string, validTo: string): Promise<void> {
    let url = `pricing/tier-range/?callingCodes=${callingCode}&validFrom=${validFrom}`;
    if (validTo) url += `&validTo=${validTo}`;
    await this.httpClient.delete<GetWhatsappTierRange>(url);
  }
}
